import React, { useState } from "react";
import * as dmsService from "../../../services/dmsService";
import * as inventoryStock from "../../../services/inventoryStockService";
import useModuleAccess from "../../../hooks/useModuleAccess";
import { hasModulePermission } from "../../../constants/platformModules";
import useFinOptions from "../../Fin/useFinOptions";
import useDmsOptions from "../useDmsOptions";
import { fmtMoney } from "../../Fin/gl/glContext";
import { DataTable, hint, linkBtn } from "../../Fin/gl/glUi";
import { ActionHistory, ChecklistForm, DmsBadge, DocHead, Panel, Section, run } from "./dmsUi";
import { AppDropdown, EmptyState, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, Tabs, formatDate, formatDateTime, inputStyle, pageStyle, todayYmd, useLoad } from "../../../components/ui";

// Vehicle stock, availability, PDI and goods transfers.
const STOCK = [
  { label: "New", value: "NEW" },
  { label: "Used", value: "USED" },
  { label: "Demo", value: "DEMO" },
];
const UNIT_STATUS = [
  ["", "In stock and reserved"],
  ["IN_STOCK", "In stock"],
  ["RESERVED", "Reserved"],
  ["ORDERED", "On order"],
  ["IN_TRANSIT", "In transit"],
  ["TRANSFER", "Being transferred"],
  ["DELIVERED", "Delivered"],
].map(([value, label]) => ({ value, label }));

function useCanManageStock() {
  const { access } = useModuleAccess();
  return hasModulePermission(access, "DMS", "DMS_SALES_MANAGER", "DMS_ADMIN");
}

/* ------------------------------ vehicle detail ------------------------------ */

// Yards: the core Inventory warehouses vehicles stand in (picked by code).
function useYards() {
  const { data } = useLoad(() => inventoryStock.listWarehouses({ status: "ACTIVE" }), []);
  return (data || []).map((w) => ({ label: `${w.code} – ${w.name}`, value: w.code }));
}

function VehicleView({ id, onHide, onChanged }) {
  const yards = useYards();
  const { data: u, loading, error, reload } = useLoad(() => dmsService.getVehicle(id), [id]);
  const manage = useCanManageStock();
  const [edit, setEdit] = useState(false);
  const refresh = () => {
    reload();
    onChanged();
  };
  return (
    <Panel title={u ? `VIN ${u.vin}` : "Vehicle"} onHide={onHide} width={860}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {u ? (
          <>
            <DocHead title="" number={`${u.make_name} ${u.model_name} ${u.variant_name}`} status={u.status} sub={`${u.stock_type.toLowerCase()} · ${u.ext_colour_name || "—"} / ${u.int_colour_name || "—"} · ${u.model_year || ""} · engine ${u.engine_no || "—"}`}>
              {["IN_STOCK", "RESERVED"].includes(u.status) && u.pdi_status !== "PASSED" && u.pdi_status !== "REQUESTED" ? <PrimaryButton onClick={() => run(() => dmsService.requestPdi({ unit_id: id }), refresh)}>Request PDI</PrimaryButton> : null}
              {manage && ["ORDERED", "IN_TRANSIT"].includes(u.status) ? <PrimaryButton onClick={() => run(() => dmsService.vehicleArrived(id, { received_date: todayYmd() }), refresh)}>Arrived</PrimaryButton> : null}
              {manage ? <SecondaryButton onClick={() => setEdit(true)}>Edit details</SecondaryButton> : null}
            </DocHead>
            <Section title="Details">
              <div className="row" style={{ fontSize: 14 }}>
                <div className="col-md-6">
                  <div>PDI: <DmsBadge status={u.pdi_status} /></div>
                  <div>Received: {formatDate(u.received_date) || "—"}</div>
                  <div>Cost: {fmtMoney(u.landed_cost)}</div>
                </div>
                <div className="col-md-6">
                  <div>Odometer: {u.odometer ?? "—"}</div>
                  <div>Key: {u.key_no || "—"}</div>
                  <div>Yard: {u.warehouse_code || "—"}</div>
                  {u.reserved_for ? <div>Reserved for {u.reserved_for.so_number} ({u.reserved_for.customer_name}) until {formatDateTime(u.reserved_until)}</div> : null}
                </div>
              </div>
              {u.notes ? <div style={{ ...hint, marginTop: 8 }}>{u.notes}</div> : null}
            </Section>
            <Section title="Movements">
              <DataTable
                rows={u.movements}
                rowKey={(m, i) => i}
                minWidth={620}
                columns={[
                  { label: "When", render: (m) => formatDateTime(m.created_at) },
                  { label: "Movement", render: (m) => m.movement.toLowerCase().replace(/_/g, " ") },
                  { label: "Status", render: (m) => (m.to_status ? <DmsBadge status={m.to_status} /> : "—") },
                  { label: "Document", render: (m) => m.doc_number || "—" },
                  { label: "Cost", num: true, render: (m) => (Number(m.cost_change) ? fmtMoney(m.cost_change) : "—") },
                  { label: "Note", render: (m) => m.comment || "" },
                ]}
              />
            </Section>
          </>
        ) : null}
      </LoadState>
      <FormDrawer
        show={edit}
        title="Vehicle details"
        initial={u ? { engine_no: u.engine_no, key_no: u.key_no, odometer: u.odometer, notes: u.notes, warehouse_code: u.warehouse_code || null } : {}}
        onHide={() => setEdit(false)}
        onDone={refresh}
        onSave={(clean) => dmsService.saveVehicle(id, { ...clean, odometer: clean.odometer === undefined ? null : Number(clean.odometer) })}
        fields={[
          { key: "engine_no", label: "Engine number", max: 40 },
          { key: "key_no", label: "Key number", max: 24 },
          { key: "warehouse_code", label: "Yard", type: "select", options: yards },
          { key: "odometer", label: "Odometer", type: "number", min: 0 },
          { key: "notes", label: "Notes", type: "textarea", max: 500 },
        ]}
      />
    </Panel>
  );
}

/* ------------------------------ stock list ------------------------------ */

function StockList() {
  const yards = useYards();
  const fin = useFinOptions(["locations"]);
  const cat = useDmsOptions(["variants", "colours:ext", "colours:int"], 0, { ext: { colour_type: "EXTERIOR" }, int: { colour_type: "INTERIOR" } });
  const manage = useCanManageStock();
  const [f, setF] = useState({ status: "", stock_type: "", location_id: null, q: "" });
  const { data, loading, error, reload } = useLoad(
    () => dmsService.listVehicles({ status: f.status || undefined, stock_type: f.stock_type || undefined, location_id: f.location_id || undefined, q: f.q || undefined, available: undefined }),
    [f.status, f.stock_type, f.location_id, f.q]
  );
  const rows = (data || []).filter((u) => f.status || ["IN_STOCK", "RESERVED"].includes(u.status));
  const [open, setOpen] = useState(null);
  const [receiving, setReceiving] = useState(false);
  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        {manage ? <PrimaryButton onClick={() => setReceiving(true)}>Receive vehicle</PrimaryButton> : null}
        <div style={{ width: 190 }}>
          <AppDropdown id="vs-st" value={f.status} options={UNIT_STATUS} onChange={(e) => setF({ ...f, status: e.value })} />
        </div>
        <div style={{ width: 140 }}>
          <AppDropdown id="vs-type" value={f.stock_type} options={[{ label: "Any stock", value: "" }, ...STOCK]} onChange={(e) => setF({ ...f, stock_type: e.value })} />
        </div>
        <div style={{ width: 200 }}>
          <AppDropdown id="vs-loc" value={f.location_id} options={[{ label: "All locations", value: null }, ...(fin.locations || [])]} onChange={(e) => setF({ ...f, location_id: e.value })} />
        </div>
        <input className="form-control" style={{ ...inputStyle, width: 220 }} placeholder="VIN, engine or model" aria-label="Search stock" onKeyDown={(e) => e.key === "Enter" && setF({ ...f, q: e.target.value.trim() })} />
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={rows}
          onRowClick={(r) => setOpen(r.id)}
          minWidth={980}
          empty={<EmptyState title="No vehicles" />}
          columns={[
            { label: "VIN", render: (r) => <b>{r.vin}</b> },
            { label: "Vehicle", render: (r) => `${r.make_name} ${r.model_name} ${r.variant_name}` },
            { label: "Colour", render: (r) => r.ext_colour_name || "—" },
            { label: "Stock", render: (r) => r.stock_type.toLowerCase() },
            { label: "PDI", render: (r) => <DmsBadge status={r.pdi_status} /> },
            { label: "Age", num: true, render: (r) => (r.age_days === null ? "—" : `${r.age_days} d`) },
            { label: "Cost", num: true, render: (r) => fmtMoney(r.landed_cost) },
            { label: "Status", render: (r) => <DmsBadge status={r.status} /> },
          ]}
        />
      </LoadState>
      {open ? <VehicleView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
      <FormDrawer
        show={receiving}
        title="Receive vehicle into stock"
        initial={{ stock_type: "NEW", status: "IN_STOCK", received_date: todayYmd(), location_id: fin.locations && fin.locations[0] ? fin.locations[0].value : null }}
        onHide={() => setReceiving(false)}
        onDone={reload}
        width={620}
        onSave={(clean, raw) => dmsService.receiveVehicle({ ...clean, landed_cost: Number(clean.landed_cost || 0), post_accounting: !raw.opening })}
        fields={[
          { key: "vin", label: "VIN / chassis number", required: true, max: 17 },
          { key: "engine_no", label: "Engine number", max: 40 },
          { key: "variant_id", label: "Variant", type: "select", required: true, options: cat.variants || [] },
          { key: "ext_colour_id", label: "Exterior colour", type: "select", options: cat.ext || [] },
          { key: "int_colour_id", label: "Interior colour", type: "select", options: cat.int || [] },
          { key: "stock_type", label: "Stock type", type: "select", required: true, options: STOCK },
          { key: "status", label: "Status", type: "select", required: true, options: [{ label: "In stock", value: "IN_STOCK" }, { label: "In transit", value: "IN_TRANSIT" }, { label: "On order", value: "ORDERED" }] },
          { key: "location_id", label: "Location", type: "select", required: true, options: fin.locations || [] },
          { key: "warehouse_code", label: "Yard", type: "select", options: yards, hint: "Left empty: the location's yard, when it has one" },
          { key: "received_date", label: "Received on", type: "date" },
          { key: "landed_cost", label: "Landed cost (ledger currency)", type: "number", step: "any", min: 0, hint: "Posts Dr vehicle inventory / Cr received not invoiced" },
          { key: "odometer", label: "Odometer", type: "number", min: 0 },
          { key: "key_no", label: "Key number", max: 24 },
          { key: "opening", label: "Opening stock (already in the opening balances: no accounting)", type: "checkbox" },
        ]}
      />
    </>
  );
}

function AvailabilitySummary() {
  const fin = useFinOptions(["locations"]);
  const [loc, setLoc] = useState(null);
  const { data, loading, error, reload } = useLoad(() => dmsService.getAvailability({ location_id: loc || undefined }), [loc]);
  return (
    <>
      <div className="mb-3" style={{ width: 240 }}>
        <AppDropdown id="av-loc" value={loc} options={[{ label: "All locations", value: null }, ...(fin.locations || [])]} onChange={(e) => setLoc(e.value)} />
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          rowKey={(r, i) => i}
          empty={<EmptyState title="No stock" />}
          columns={[
            { label: "Make", render: (r) => r.make_name },
            { label: "Model", render: (r) => r.model_name },
            { label: "Variant", render: (r) => r.variant_name },
            { label: "Stock", render: (r) => r.stock_type.toLowerCase() },
            { label: "Available", num: true, render: (r) => <b style={{ color: r.in_stock ? "#065F46" : "#991B1B" }}>{r.in_stock}</b> },
            { label: "Reserved", num: true, render: (r) => r.reserved },
            { label: "Incoming", num: true, render: (r) => r.incoming },
            { label: "Transferring", num: true, render: (r) => r.in_transfer },
          ]}
        />
      </LoadState>
    </>
  );
}

export function VehicleStock() {
  const [tab, setTab] = useState("stock");
  return (
    <div style={pageStyle}>
      <PageHeader title="Vehicle Stock" subtitle="Every vehicle by VIN, its status and history" />
      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: "stock", label: "Vehicles" },
          { key: "availability", label: "Availability" },
        ]}
      />
      {tab === "stock" ? <StockList /> : <AvailabilitySummary />}
    </div>
  );
}

export function VehicleAvailability() {
  return (
    <div style={pageStyle}>
      <PageHeader title="Vehicle Availability" subtitle="What can be sold now, what is reserved and what is coming" />
      <AvailabilitySummary />
    </div>
  );
}

/* ------------------------------ PDI ------------------------------ */

function PdiView({ id, onHide, onChanged }) {
  const { data: p, loading, error, reload } = useLoad(() => dmsService.getPdi(id), [id]);
  const [defect, setDefect] = useState(false);
  const [fail, setFail] = useState(false);
  const refresh = () => {
    reload();
    onChanged();
  };
  const open = p && ["REQUESTED", "IN_PROGRESS"].includes(p.status);
  return (
    <Panel title={p ? `PDI ${p.pdi_number}` : "PDI"} onHide={onHide} width={860}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {p ? (
          <>
            <DocHead title="" number={`${p.vehicle.make_name} ${p.vehicle.model_name} · VIN ${p.vehicle.vin}`} status={p.status} sub={`Requested ${formatDate(p.requested_date)}${p.technician_name ? ` · ${p.technician_name}` : ""}`}>
              {p.status === "REQUESTED" ? <PrimaryButton onClick={() => run(() => dmsService.pdiAction(id, "start"), refresh)}>Start</PrimaryButton> : null}
              {p.status === "IN_PROGRESS" ? <PrimaryButton onClick={() => run(() => dmsService.pdiAction(id, "complete", { result: "PASSED" }), refresh)}>Pass</PrimaryButton> : null}
              {p.status === "IN_PROGRESS" ? <SecondaryButton onClick={() => setFail(true)}>Fail</SecondaryButton> : null}
              {open ? <SecondaryButton onClick={() => setDefect(true)}>Add defect</SecondaryButton> : null}
              {open ? <SecondaryButton onClick={() => window.confirm("Cancel this PDI?") && run(() => dmsService.pdiAction(id, "cancel"), refresh)}>Cancel</SecondaryButton> : null}
            </DocHead>
            <Section title="Checklist">
              <ChecklistForm checklist={p.checklist} disabled={!open} onSave={(body) => run(() => dmsService.pdiAction(id, "checklist", body), reload)} />
            </Section>
            <Section title="Defects">
              <DataTable
                rows={p.defects}
                empty={<div style={hint}>No defects recorded.</div>}
                columns={[
                  { label: "Defect", render: (d) => d.description },
                  { label: "Severity", render: (d) => d.severity.toLowerCase() },
                  { label: "Status", render: (d) => <DmsBadge status={d.status === "FIXED" ? "PASSED" : "OPEN"} /> },
                  { label: "", render: (d) => (open && d.status === "OPEN" ? <button type="button" style={linkBtn} onClick={() => run(() => dmsService.fixPdiDefect(id, d.id), reload)}>Fixed</button> : null) },
                ]}
              />
            </Section>
            <Section title="Action history">
              <ActionHistory actions={p.actions} />
            </Section>
          </>
        ) : null}
      </LoadState>
      <FormDrawer
        show={defect}
        title="Add defect"
        initial={{ severity: "MINOR" }}
        onHide={() => setDefect(false)}
        onDone={reload}
        onSave={(clean) => dmsService.pdiAction(id, "defects", clean)}
        fields={[
          { key: "description", label: "Defect", type: "textarea", required: true, max: 500 },
          { key: "severity", label: "Severity", type: "select", required: true, options: [{ label: "Minor", value: "MINOR" }, { label: "Major (blocks the pass)", value: "MAJOR" }] },
        ]}
      />
      <FormDrawer show={fail} title="Fail the vehicle" initial={{}} onHide={() => setFail(false)} onDone={refresh} onSave={(clean) => dmsService.pdiAction(id, "complete", { result: "FAILED", remarks: clean.remarks })} fields={[{ key: "remarks", label: "Why", type: "textarea", required: true, max: 1000 }]} />
    </Panel>
  );
}

export function PdiRequests() {
  const [status, setStatus] = useState("");
  const { data, loading, error, reload } = useLoad(() => dmsService.listPdi({ status: status || undefined }), [status]);
  const [open, setOpen] = useState(null);
  return (
    <div style={pageStyle}>
      <PageHeader title="PDI Requests" subtitle="Pre-delivery inspections before vehicles are reserved and handed over" />
      <div className="mb-3" style={{ width: 200 }}>
        <AppDropdown
          id="pdi-st"
          value={status}
          options={[
            { label: "All", value: "" },
            { label: "Requested", value: "REQUESTED" },
            { label: "In progress", value: "IN_PROGRESS" },
            { label: "Passed", value: "PASSED" },
            { label: "Failed", value: "FAILED" },
          ]}
          onChange={(e) => setStatus(e.value)}
        />
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No PDI requests" text="Request PDI from a vehicle in Vehicle Stock." />}
          columns={[
            { label: "Number", render: (r) => <b>{r.pdi_number}</b> },
            { label: "VIN", render: (r) => r.vin },
            { label: "Vehicle", render: (r) => `${r.model_name} ${r.variant_name}` },
            { label: "Technician", render: (r) => r.technician_name || "—" },
            { label: "Requested", render: (r) => formatDate(r.requested_date) },
            { label: "Open defects", num: true, render: (r) => r.open_defects || "—" },
            { label: "Status", render: (r) => <DmsBadge status={r.status} /> },
          ]}
        />
      </LoadState>
      {open ? <PdiView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
    </div>
  );
}

/* ------------------------------ transfers ------------------------------ */

function TransferView({ id, onHide, onChanged }) {
  const { data: t, loading, error, reload } = useLoad(() => dmsService.getTransfer(id), [id]);
  const refresh = () => {
    reload();
    onChanged();
  };
  const doIt = (a) => run(() => dmsService.transferAction(id, a), refresh);
  return (
    <Panel title={t ? `Transfer ${t.transfer_number}` : "Transfer"} onHide={onHide} width={820}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {t ? (
          <>
            <DocHead title="" number={`${t.from_location_name} → ${t.to_location_name}`} status={t.status} sub={`${formatDate(t.transfer_date)}${t.driver_name ? ` · driver ${t.driver_name}` : ""}`}>
              {t.status === "DRAFT" ? <PrimaryButton onClick={() => doIt("submit")}>Submit</PrimaryButton> : null}
              {t.status === "APPROVED" ? <PrimaryButton onClick={() => doIt("ship")}>Ship (transfer out)</PrimaryButton> : null}
              {t.status === "SHIPPED" ? <PrimaryButton onClick={() => doIt("receive")}>Receive (transfer in)</PrimaryButton> : null}
              {["DRAFT", "APPROVED"].includes(t.status) ? <SecondaryButton onClick={() => window.confirm("Cancel this transfer?") && doIt("cancel")}>Cancel</SecondaryButton> : null}
            </DocHead>
            <Section title="Vehicles">
              <DataTable
                rows={t.lines}
                columns={[
                  { label: "VIN", render: (l) => l.vin },
                  { label: "Vehicle", render: (l) => `${l.model_name} ${l.variant_name}` },
                  { label: "Status", render: (l) => <DmsBadge status={l.status} /> },
                  { label: "Cost", num: true, render: (l) => (Number(l.unit_cost) ? fmtMoney(l.unit_cost) : "—") },
                ]}
              />
            </Section>
            <Section title="Action history">
              <ActionHistory actions={t.actions} />
            </Section>
          </>
        ) : null}
      </LoadState>
    </Panel>
  );
}

export function GoodsTransfers() {
  const fin = useFinOptions(["locations"]);
  const { data, loading, error, reload } = useLoad(() => dmsService.listTransfers({}), []);
  const stock = useLoad(() => dmsService.listVehicles({ status: "IN_STOCK", size: 500 }), []);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  return (
    <div style={pageStyle}>
      <PageHeader title="Goods Transfers" subtitle="Move vehicles between showrooms: transfer out, in transit, transfer in" actions={<PrimaryButton onClick={() => setCreating(true)}>New transfer</PrimaryButton>} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No transfers" />}
          columns={[
            { label: "Number", render: (r) => <b>{r.transfer_number}</b> },
            { label: "Date", render: (r) => formatDate(r.transfer_date) },
            { label: "From", render: (r) => r.from_location_name || "—" },
            { label: "To", render: (r) => r.to_location_name || "—" },
            { label: "Vehicles", num: true, render: (r) => r.vehicles },
            { label: "Status", render: (r) => <DmsBadge status={r.status} /> },
          ]}
        />
      </LoadState>
      {open ? <TransferView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
      <FormDrawer
        show={creating}
        title="New transfer"
        initial={{ transfer_date: todayYmd(), unit_ids: [] }}
        onHide={() => setCreating(false)}
        onDone={(d) => {
          reload();
          if (d && d.id) setOpen(d.id);
        }}
        width={640}
        onSave={(clean) => dmsService.createTransfer(clean)}
        fields={[
          { key: "from_location_id", label: "From", type: "select", required: true, options: fin.locations || [] },
          { key: "to_location_id", label: "To", type: "select", required: true, options: fin.locations || [] },
          { key: "transfer_date", label: "Date", type: "date", required: true },
          { key: "driver_name", label: "Driver", max: 150 },
          { key: "remarks", label: "Remarks", type: "textarea", max: 1000 },
        ]}
      >
        {(values, setValues) => {
          const from = (fin.locations || []).find((o) => o.value === values.from_location_id);
          const units = (stock.data || []).filter((u) => from && u.location_code === from.code);
          const chosen = values.unit_ids || [];
          return (
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Vehicles at the sending location</div>
              {units.length === 0 ? <div style={hint}>Choose the sending location to see its vehicles in stock.</div> : null}
              {units.map((u) => (
                <label key={u.id} className="d-flex gap-2" style={{ fontSize: 14 }}>
                  <input type="checkbox" checked={chosen.includes(u.id)} onChange={(e) => setValues((s) => ({ ...s, unit_ids: e.target.checked ? [...chosen, u.id] : chosen.filter((x) => x !== u.id) }))} />
                  {u.vin} · {u.model_name} {u.variant_name} · {u.ext_colour_name || ""}
                </label>
              ))}
            </div>
          );
        }}
      </FormDrawer>
    </div>
  );
}
